/**
 * CustomTimePicker Component
 *
 * Reusable time picker with optimal time recommendations and availability indicators.
 * Shows a hero card for recommended time and alternative time pills with visual indicators.
 *
 * @module components/CustomTimePicker
 */

import { FC, useState, useEffect, useMemo, useCallback, useRef } from "react"
import { View, ViewStyle, TextStyle, TouchableOpacity, ActivityIndicator } from "react-native"
import dayjs from "dayjs"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Text } from "@/components/Text"
import {
  type OpeningHoursResponse,
  type OptimalBookingTimesResponse,
  type WeeklySchedule,
} from "@/services/firebase/functions"
import { colors, spacing } from "@/theme"
import {
  isGoodTime as isGoodTimeUtil,
  isMediumTime as isMediumTimeUtil,
  isBusyTime as isBusyTimeUtil,
} from "@/utils/dateTimeUtils"

/**
 * How far ahead `findNextOpenDay` looks for a bookable day. A month covers hub
 * holiday closures in `specialDates` without walking forward indefinitely.
 */
const NEXT_OPEN_DAY_SEARCH_DAYS = 30

export interface CustomTimePickerProps {
  /**
   * Whether the time picker is visible
   */
  visible: boolean

  /**
   * Callback when a time is applied/selected
   */
  onApply: (time: string) => void

  /**
   * Callback when closing with selected time
   */
  onClose: (selectedTime: string) => void

  /**
   * The selected date (YYYY-MM-DD format)
   */
  selectedDate: string

  /**
   * Initial time value (HH:MM format)
   */
  initialTime: string

  /**
   * Opening hours data from backend
   */
  openingHours: OpeningHoursResponse | null | undefined

  /**
   * Optimal times data from backend
   */
  optimalTimes: OptimalBookingTimesResponse | null | undefined

  /**
   * Whether optimal times are currently loading
   */
  loadingOptimalTimes: boolean

  /**
   * Whether the optimal times fetch timed out
   */
  fetchTimeout?: boolean

  /**
   * Optional callback to change the selected date
   */
  onDateChange?: (date: string) => void

  /**
   * Optional predicate for dates the caller's business rules forbid regardless of
   * hub opening hours (e.g. HBS orders can't land on a Saturday, ADR-0051 §5).
   * `findNextOpenDay`'s auto-advance skips any date this returns true for, so it
   * never lands the picker on a day that will just clear itself again.
   */
  isDateBlocked?: (date: dayjs.Dayjs) => boolean

  /**
   * Optional callback to switch to driveby service type.
   */
  onSwitchToDriveby?: () => void

  /**
   * Optional callback to go back to the previous step (e.g., date selection).
   */
  onGoBack?: () => void
}

/**
 * Custom Time Picker Component
 */
export const CustomTimePicker: FC<CustomTimePickerProps> = ({
  visible,
  onApply,
  onClose,
  selectedDate,
  openingHours,
  optimalTimes,
  loadingOptimalTimes,
  fetchTimeout = false,
  onDateChange,
  isDateBlocked,
  onSwitchToDriveby,
  onGoBack,
}) => {
  // Use optimal recommended time ONLY if available, not loading, and still feasible.
  // Guards against stale optimalTimes where the backend's pick is no longer bookable.
  const recommendedTime = useMemo(() => {
    if (loadingOptimalTimes || !optimalTimes?.recommendedTime) return null
    const picked = optimalTimes.recommendedTime
    const slot = optimalTimes.allSlots?.find((s) => s.time === picked)
    return slot?.isFeasible ? picked : null
  }, [loadingOptimalTimes, optimalTimes])

  const [selectedTime, setSelectedTime] = useState<string>("")
  const [error, setError] = useState<string | null>(null)
  const [showAllTimes, setShowAllTimes] = useState(false)
  const [advancePending, setAdvancePending] = useState(false)
  const autoAdvancedFromRef = useRef<Set<string>>(new Set())

  /**
   * Latches once the "today is already over" check below has had a chance to
   * run for this opening of the picker.
   *
   * Callers are not required to hand us a stable `onDateChange`: before
   * PLA-715 `B2cRescheduleModal` passed a fresh inline arrow on every render,
   * so the check re-fired each time the parent re-rendered — including right
   * after its slot fetch resolved. Each re-fire blanked the freshly fetched
   * slots without changing the date, so nothing refetched and the time step
   * ended up with no pills, no spinner and no empty state.
   */
  const afterCloseCheckedRef = useRef(false)

  // Generate available time slots — only feasible backend slots are selectable.
  // Infeasible slots (busyTimes / capacity-exhausted) are dropped entirely so the
  // user cannot rebook into a slot the hub can't fulfil. Empty result then trips
  // the auto-advance effect below, mirroring the booking widget's behaviour.
  const timeSlots = useMemo(() => {
    if (loadingOptimalTimes) return []

    if (optimalTimes?.allSlots && optimalTimes.allSlots.length > 0 && !fetchTimeout) {
      return optimalTimes.allSlots
        .filter((slot) => slot.isFeasible)
        .map((slot) => slot.time)
        .sort((a, b) => a.localeCompare(b))
    }

    // No fallback — empty slots trigger the redirect UI
    return [] as string[]
  }, [optimalTimes, fetchTimeout, loadingOptimalTimes])

  // Filter out recommended time from alternatives
  const alternativeTimes = timeSlots.filter((time) => time !== recommendedTime)

  // Soonest time from ALL slots (including recommended)
  const soonestTime = timeSlots.length > 0 ? timeSlots[0] : null

  // Top alternatives: soonest (if not recommended) + next-best scored
  const topAlternatives = useMemo(() => {
    if (!optimalTimes?.allSlots || alternativeTimes.length <= 3) return alternativeTimes

    const picks: string[] = []

    // Include soonest if it's not already the recommended time
    if (soonestTime && soonestTime !== recommendedTime) {
      picks.push(soonestTime)
    }

    // Fill remaining spots with best-scored alternatives (excluding already picked)
    const remaining = alternativeTimes.filter((t) => !picks.includes(t))
    const scored = remaining.map((time) => ({
      time,
      score: optimalTimes.allSlots.find((s) => s.time === time)?.score ?? 0,
    }))
    scored.sort((a, b) => b.score - a.score)

    const slotsNeeded = 2 - picks.length
    for (let i = 0; i < slotsNeeded && i < scored.length; i++) {
      picks.push(scored[i].time)
    }

    return picks.sort((a, b) => a.localeCompare(b))
  }, [alternativeTimes, optimalTimes, soonestTime, recommendedTime])

  const remainingTimes = alternativeTimes.filter((t) => !topAlternatives.includes(t))
  const displayedAlternatives = showAllTimes ? alternativeTimes : topAlternatives

  // Reset expansion when data changes
  useEffect(() => {
    setShowAllTimes(false)
  }, [optimalTimes])

  // Helper function to find next open day.
  // Returns null when no bookable day exists inside the search window — the
  // caller must then leave the date alone and let its own empty state render,
  // rather than advancing onto a closed or blocked day.
  const findNextOpenDay = useCallback(
    (startDate: Date): string | null => {
      const checkDate = new Date(startDate)
      checkDate.setDate(checkDate.getDate() + 1)

      if (!openingHours?.schedule) {
        // Without a schedule the caller's business rules are the only signal we
        // have, so return the first day they don't forbid.
        for (let i = 0; i < NEXT_OPEN_DAY_SEARCH_DAYS; i++) {
          if (!isDateBlocked?.(dayjs(checkDate))) {
            return dayjs(checkDate).format("YYYY-MM-DD")
          }
          checkDate.setDate(checkDate.getDate() + 1)
        }
        return null
      }

      for (let i = 0; i < NEXT_OPEN_DAY_SEARCH_DAYS; i++) {
        const dayOfWeek = checkDate.getDay()
        const days = [
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
        ] as const
        const dayName = days[dayOfWeek] as keyof typeof openingHours.schedule
        const dateString = dayjs(checkDate).format("YYYY-MM-DD")

        if (!isDateBlocked?.(dayjs(checkDate))) {
          const specialDate = openingHours.specialDates?.[dateString]
          if (specialDate) {
            if (specialDate.isOpen) {
              return dateString
            }
          } else if (openingHours.schedule[dayName]?.isOpen) {
            return dateString
          }
        }

        checkDate.setDate(checkDate.getDate() + 1)
      }

      // Every candidate in the window was closed or blocked. Falling back to a
      // fixed "tomorrow" here would hand back a date the caller has to blank
      // again (ADR-0051 §5), so report that there is nothing to advance to.
      return null
    },
    [openingHours, isDateBlocked],
  )

  // Clear selection when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedTime("")
      setError(null)

      // Auto-select next open day if current time is after closing time.
      // Runs at most once per open — see `afterCloseCheckedRef`.
      if (onDateChange && openingHours?.schedule && !afterCloseCheckedRef.current) {
        afterCloseCheckedRef.current = true

        const today = new Date()
        const currentTime = dayjs(today).format("HH:mm")
        const dayOfWeek = today.getDay()
        const days = [
          "sunday",
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
        ] as const
        const dayName = days[dayOfWeek] as keyof typeof openingHours.schedule
        const daySchedule = openingHours.schedule[dayName]

        // The intent of this branch is "today is over, don't offer today", so
        // it only applies while the selection still points at today (or at a
        // date that has already passed). A customer who deliberately picked a
        // later day keeps it.
        const selectionNeedsAdvancing =
          !selectedDate || !dayjs(selectedDate).isAfter(dayjs(today), "day")

        if (daySchedule?.close && currentTime >= daySchedule.close && selectionNeedsAdvancing) {
          const nextOpenDay = findNextOpenDay(today)
          if (nextOpenDay && nextOpenDay !== selectedDate) {
            onDateChange(nextOpenDay)
          }
        }
      }
    } else {
      autoAdvancedFromRef.current = new Set()
      afterCloseCheckedRef.current = false
      setAdvancePending(false)
    }
  }, [visible, openingHours, findNextOpenDay, onDateChange, selectedDate])

  // Clear advance-pending flag when parent picks up the date change (or user selects manually)
  useEffect(() => {
    setAdvancePending(false)
  }, [selectedDate])

  // Auto-advance to the next open day when the current date returns no feasible slots.
  // Cap at 14 consecutive advances; ignore fetch failures (those render the timeout UI).
  useEffect(() => {
    if (!onDateChange) return
    if (loadingOptimalTimes || fetchTimeout) return
    if (!optimalTimes || !selectedDate || !openingHours?.schedule) return
    // Only act on results that belong to the currently selected date. Some
    // parents (e.g. RescheduleInspectionModal) leave stale optimalTimes in
    // place across a date change, which otherwise pollutes autoAdvancedFromRef.
    if (optimalTimes.metadata?.date !== selectedDate) return
    if (timeSlots.length > 0) return
    if (autoAdvancedFromRef.current.has(selectedDate)) return
    if (autoAdvancedFromRef.current.size >= 14) return

    const currentDateObj = new Date(selectedDate + "T12:00:00")
    const nextDay = findNextOpenDay(currentDateObj)
    if (!nextDay || nextDay === selectedDate) return

    autoAdvancedFromRef.current.add(selectedDate)
    setAdvancePending(true)

    const timer = setTimeout(() => {
      onDateChange(nextDay)
    }, 300)

    return () => clearTimeout(timer)
  }, [
    timeSlots.length,
    loadingOptimalTimes,
    fetchTimeout,
    optimalTimes,
    selectedDate,
    openingHours,
    onDateChange,
    findNextOpenDay,
  ])

  const formatOpeningHoursForDate = (): string => {
    if (!openingHours?.schedule) return "09:00-17:00"

    const dateObj = dayjs(selectedDate)
    const dateStr = dateObj.format("YYYY-MM-DD")

    // Check special dates
    if (openingHours.specialDates && openingHours.specialDates[dateStr]) {
      const specialDate = openingHours.specialDates[dateStr]
      if (!specialDate.isOpen) return "Geschlossen"
      return `${specialDate.open}-${specialDate.close}`
    }

    // Get weekday schedule
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
    const dayName = dayNames[dateObj.day()] as keyof WeeklySchedule
    const daySchedule = openingHours.schedule[dayName]

    if (!daySchedule || !daySchedule.isOpen) return "Geschlossen"
    return `${daySchedule.open}-${daySchedule.close}`
  }

  const handleTimeSelection = (time: string) => {
    setSelectedTime(time)
    onApply(time)
    setError(null)
    onClose(time)
  }

  const isGoodTime = (time: string): boolean => isGoodTimeUtil(time, optimalTimes)
  const isMediumTime = (time: string): boolean => isMediumTimeUtil(time, optimalTimes)
  const isBusyTime = (time: string): boolean => isBusyTimeUtil(time, optimalTimes)

  // Helper to get pill styling based on optimization data
  const getTimeSlotStyle = (time: string) => {
    if (isGoodTime(time)) {
      return $goodTimePill
    }
    if (isMediumTime(time)) {
      return $mediumTimePill
    }
    if (isBusyTime(time)) {
      return $busyTimePill
    }
    return null
  }

  return (
    <>
      {/* Loading indicator for optimal times (also shown while auto-advancing to next day) */}
      {(loadingOptimalTimes || advancePending) && (
        <View style={$loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={$loadingText}>
            {advancePending
              ? "Nächsten verfügbaren Tag wird geladen..."
              : "Verfügbare Zeiten werden geladen..."}
          </Text>
        </View>
      )}

      {/* Empty state: no slots from backend */}
      {!loadingOptimalTimes &&
        !advancePending &&
        optimalTimes != null &&
        timeSlots.length === 0 &&
        !fetchTimeout && (
          <View style={$noSlotsContainer}>
            <Icon name="event-busy" size={48} color={colors.palette.neutral450} />
            <Text style={$noSlotsTitle}>Keine Termine verfügbar</Text>
            <Text style={$noSlotsText}>
              Für diesen Tag sind leider keine Abholzeiten mehr frei.
            </Text>
            {onSwitchToDriveby && (
              <TouchableOpacity
                style={$noSlotsDrivebyButton}
                onPress={onSwitchToDriveby}
                activeOpacity={0.7}
              >
                <Icon name="directions-car" size={20} color={colors.surface} />
                <Text style={$noSlotsDrivebyText}>Selbst vorfahren</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={$noSlotsDateButton} onPress={onGoBack} activeOpacity={0.7}>
              <Icon name="calendar-today" size={18} color={colors.modalHelperText} />
              <Text style={$noSlotsDateText}>Anderes Datum wählen</Text>
            </TouchableOpacity>
          </View>
        )}

      {/* Error state: timeout or fetch failed */}
      {!loadingOptimalTimes && timeSlots.length === 0 && fetchTimeout && (
        <View style={$noSlotsContainer}>
          <Icon name="error-outline" size={48} color={colors.amber} />
          <Text style={$noSlotsTitle}>Termine konnten nicht geladen werden</Text>
          <Text style={$noSlotsText}>Bitte buche telefonisch oder per WhatsApp.</Text>
        </View>
      )}

      {!loadingOptimalTimes && timeSlots.length > 0 && (
        <View>
          {/* Legend for availability indicators */}
          <View style={$legend}>
            <Text style={$legendTitle}>Öffnungszeiten: {formatOpeningHoursForDate()} Uhr</Text>

            <View style={$legendItem}>
              <Icon name="check-circle" size={16} color={colors.emerald} />
              <Text style={$legendText}>Keine bis kurze Wartezeiten.</Text>
            </View>

            <View style={$legendItem}>
              <Icon name="check-circle" size={16} color={colors.infoBlue} />
              <Text style={$legendText}>Keine bis mittlere Wartezeiten.</Text>
            </View>

            {optimalTimes && optimalTimes.busyTimes.length > 0 && (
              <View style={$legendItem}>
                <Icon name="warning" size={16} color={colors.amber} />
                <Text style={$legendText}>
                  Hohe Auslastung: Kurze bis längere Wartezeiten möglich.
                </Text>
              </View>
            )}

            {/* Driveby alternative hint when few pickup_delivery slots available */}
            {onSwitchToDriveby &&
              optimalTimes &&
              optimalTimes.goodTimes.length + optimalTimes.mediumTimes.length < 10 && (
                <Text style={$drivebyHintText}>
                  Dein Termin ist nicht dabei aber du hast es eilig? Buche alternativ einen{" "}
                  <Text style={$drivebyHintLink} onPress={onSwitchToDriveby}>
                    Termin vor Ort.
                  </Text>
                </Text>
              )}
          </View>

          {/* Hero Card - Recommended Time */}
          {recommendedTime && (
            <TouchableOpacity
              style={[$heroTimeCard, selectedTime === recommendedTime && $heroTimeCardSelected]}
              onPress={() => handleTimeSelection(recommendedTime)}
              activeOpacity={0.7}
            >
              {/* Recommended badge */}
              {optimalTimes?.recommendedTime === recommendedTime && (
                <View style={$recommendedBadge}>
                  <Icon name="check" size={12} color={colors.surface} />
                  <Text style={$recommendedBadgeText}>Empfohlen</Text>
                </View>
              )}
              <Icon
                name="schedule"
                size={32}
                color={colors.surface}
                style={{ marginRight: spacing.md }}
              />
              <View style={$heroTimeContent}>
                <Text
                  style={[$heroTimeText, selectedTime === recommendedTime && $heroTimeTextSelected]}
                >
                  {recommendedTime}
                </Text>
                <Text
                  style={[
                    $heroTimeSubtitle,
                    selectedTime === recommendedTime && $heroTimeSubtitleSelected,
                  ]}
                >
                  {recommendedTime === soonestTime
                    ? "Beste Verfügbarkeit · Frühester Termin"
                    : "Beste Verfügbarkeit"}
                </Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Alternative Times Section */}
          {alternativeTimes.length > 0 && (
            <>
              <View style={$alternativesHeader}>
                <View style={$alternativesDividerLine} />
                <Text style={$alternativesHeaderText}>Andere Zeiten wählen:</Text>
                <View style={$alternativesDividerLine} />
              </View>

              {/* Top picks — wider pills with badge labels, tight row */}
              {!showAllTimes && (
                <View style={$topPicksRow}>
                  {displayedAlternatives.map((time) => {
                    const isSelected = time === selectedTime
                    const isSoonest = time === soonestTime && recommendedTime !== soonestTime
                    const specialStyle = getTimeSlotStyle(time)

                    return (
                      <TouchableOpacity
                        key={time}
                        style={[
                          $topPickPill,
                          specialStyle,
                          isSelected && $alternativeTimePillSelected,
                        ]}
                        onPress={() => handleTimeSelection(time)}
                        activeOpacity={0.7}
                      >
                        {isSoonest && !isSelected && (
                          <View style={$topPickBadge}>
                            <Icon name="bolt" size={10} color={colors.surface} />
                            <Text style={$topPickBadgeText}>Frühester</Text>
                          </View>
                        )}
                        <View style={$topPickContent}>
                          <Text
                            style={[
                              $topPickTimeText,
                              isGoodTime(time) && !isSelected && $goodTimeText,
                              isMediumTime(time) && !isSelected && $mediumTimeText,
                              isBusyTime(time) && !isSelected && $busyTimeText,
                              isSelected && $alternativeTimePillTextSelected,
                            ]}
                          >
                            {time}
                          </Text>
                          {!isSelected && isGoodTime(time) && (
                            <Icon name="check-circle" size={16} color={colors.emerald} />
                          )}
                          {!isSelected && isMediumTime(time) && (
                            <Icon name="check-circle" size={16} color={colors.infoBlue} />
                          )}
                        </View>
                      </TouchableOpacity>
                    )
                  })}
                </View>
              )}

              {/* Expanded grid — all slots in standard pills */}
              {showAllTimes && (
                <View style={$alternativeTimesContainer}>
                  {alternativeTimes.map((time) => {
                    const isSelected = time === selectedTime
                    const specialStyle = getTimeSlotStyle(time)

                    return (
                      <TouchableOpacity
                        key={time}
                        style={[
                          $alternativeTimePill,
                          specialStyle,
                          isSelected && $alternativeTimePillSelected,
                        ]}
                        onPress={() => handleTimeSelection(time)}
                        activeOpacity={0.7}
                      >
                        <View style={$timePillContent}>
                          <Text
                            style={[
                              $alternativeTimePillText,
                              isGoodTime(time) && !isSelected && $goodTimeText,
                              isMediumTime(time) && !isSelected && $mediumTimeText,
                              isBusyTime(time) && !isSelected && $busyTimeText,
                              isSelected && $alternativeTimePillTextSelected,
                            ]}
                          >
                            {time}
                          </Text>
                          {!isSelected && isGoodTime(time) && (
                            <Icon
                              name="check-circle"
                              size={14}
                              color={colors.emerald}
                              style={$iconMargin}
                            />
                          )}
                          {!isSelected && isMediumTime(time) && (
                            <Icon
                              name="check-circle"
                              size={14}
                              color={colors.infoBlue}
                              style={$iconMargin}
                            />
                          )}
                          {!isSelected && isBusyTime(time) && (
                            <Icon
                              name="warning"
                              size={14}
                              color={colors.amber}
                              style={$iconMargin}
                            />
                          )}
                        </View>
                      </TouchableOpacity>
                    )
                  })}
                </View>
              )}

              {/* Expand / collapse button */}
              {remainingTimes.length > 0 && (
                <TouchableOpacity
                  style={$expandButton}
                  onPress={() => setShowAllTimes(!showAllTimes)}
                  activeOpacity={0.7}
                >
                  <Text style={$expandButtonText}>
                    {showAllTimes
                      ? "Weniger anzeigen"
                      : `Weitere ${remainingTimes.length} Zeiten anzeigen`}
                  </Text>
                  <Icon
                    name={showAllTimes ? "expand-less" : "expand-more"}
                    size={20}
                    color={colors.modalHelperText}
                  />
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      )}

      {error && (
        <View style={$errorContainer}>
          <Icon name="error-outline" size={18} color={colors.error} />
          <Text style={$errorText}>{error}</Text>
        </View>
      )}
    </>
  )
}

// Styles
// ─── Empty / Error States ────────────────────────────────────────────

const $noSlotsContainer: ViewStyle = {
  alignItems: "center",
  paddingVertical: 32,
  gap: 12,
}

const $noSlotsTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
}

const $noSlotsText: TextStyle = {
  fontSize: 14,
  color: colors.modalHelperText,
  textAlign: "center",
  paddingHorizontal: 16,
}

const $noSlotsDrivebyButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  backgroundColor: colors.emerald,
  paddingVertical: 12,
  paddingHorizontal: 24,
  borderRadius: 10,
  marginTop: 8,
}

const $noSlotsDrivebyText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
}

const $noSlotsDateButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
  paddingVertical: 8,
}

const $noSlotsDateText: TextStyle = {
  color: colors.modalHelperText,
  fontSize: 14,
  fontWeight: "500",
}

// ─── Loading ─────────────────────────────────────────────────────────

const $loadingContainer: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xl,
}

const $loadingText: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontWeight: "500",
  marginTop: spacing.sm,
}

const $legend: ViewStyle = {
  marginTop: spacing.md,
  marginBottom: spacing.lg,
  padding: spacing.md,
  backgroundColor: colors.modalInputBg,
  borderRadius: 8,
}

const $legendTitle: TextStyle = {
  fontSize: 13,
  color: colors.text,
  marginBottom: spacing.sm,
  lineHeight: 18,
}

const $legendItem: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  marginTop: spacing.xs,
  gap: spacing.xs,
}

const $legendText: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  flex: 1,
  lineHeight: 16,
}

const $drivebyHintText: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  lineHeight: 16,
  marginTop: spacing.sm,
}

const $drivebyHintLink: TextStyle = {
  fontSize: 12,
  lineHeight: 16,
  color: colors.primary,
  fontWeight: "600",
  cursor: "pointer" as any,
}

const $heroTimeCard: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.emerald,
  borderRadius: 12,
  borderWidth: 2,
  borderColor: colors.emerald,
  paddingVertical: 24,
  paddingHorizontal: 20,
  marginHorizontal: 16,
  // @ts-ignore - Web-specific shadow
  boxShadow: "0 4px 12px rgba(17, 30, 44, 0.18)",
  elevation: 4,
}

const $heroTimeCardSelected: ViewStyle = {
  backgroundColor: colors.emeraldDark,
  borderColor: colors.emeraldDark,
}

const $heroTimeContent: ViewStyle = {
  flex: 1,
}

const $heroTimeText: TextStyle = {
  fontSize: 28,
  fontWeight: "700",
  color: colors.surface,
  marginBottom: 4,
}

const $heroTimeTextSelected: TextStyle = {
  color: colors.surface,
}

const $heroTimeSubtitle: TextStyle = {
  fontSize: 14,
  color: colors.surface,
  fontWeight: "500",
  opacity: 0.9,
}

const $heroTimeSubtitleSelected: TextStyle = {
  color: colors.surface,
  opacity: 0.9,
}

const $recommendedBadge: ViewStyle = {
  position: "absolute",
  top: spacing.sm,
  right: spacing.sm,
  backgroundColor: "rgba(255, 255, 255, 0.25)",
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
  paddingVertical: 4,
  paddingHorizontal: spacing.xs,
  borderRadius: 12,
}

const $recommendedBadgeText: TextStyle = {
  fontSize: 11,
  color: colors.surface,
  fontWeight: "600",
}

const $alternativesHeader: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  marginVertical: spacing.md,
  gap: spacing.sm,
}

const $alternativesDividerLine: ViewStyle = {
  flex: 1,
  height: 1,
  backgroundColor: colors.border,
}

const $alternativesHeaderText: TextStyle = {
  fontSize: 13,
  fontWeight: "600",
  color: colors.modalHelperText,
  paddingHorizontal: spacing.xs,
}

const $alternativeTimesContainer: ViewStyle = {
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 10,
  rowGap: 12,
  marginBottom: spacing.md,
  justifyContent: "space-between",
}

const $alternativeTimePill: ViewStyle = {
  paddingVertical: 10,
  paddingHorizontal: 16,
  backgroundColor: colors.surfaceSecondary,
  borderRadius: 8,
  width: 78,
  alignItems: "center",
  justifyContent: "center",
}

const $alternativeTimePillSelected: ViewStyle = {
  backgroundColor: colors.primary,
}

const $alternativeTimePillText: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.text,
}

const $alternativeTimePillTextSelected: TextStyle = {
  color: colors.surface,
}

const $goodTimePill: ViewStyle = {
  borderWidth: 2,
  borderColor: colors.emerald,
  backgroundColor: colors.successBgLight,
}

const $mediumTimePill: ViewStyle = {
  borderWidth: 2,
  borderColor: colors.infoBlue,
  backgroundColor: colors.infoBgLight,
}

const $busyTimePill: ViewStyle = {
  borderWidth: 2,
  borderColor: colors.amber,
  backgroundColor: colors.highlightYellow,
}

const $timePillContent: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
}

const $goodTimeText: TextStyle = {
  color: colors.emeraldDark,
}

const $mediumTimeText: TextStyle = {
  color: colors.infoBlueDark,
}

const $busyTimeText: TextStyle = {
  color: colors.amberDark,
}

const $iconMargin: ViewStyle = {
  marginLeft: 4,
}

// ─── Top Picks Row (collapsed view) ─────────────────────────────────

const $topPicksRow: ViewStyle = {
  flexDirection: "row",
  gap: 10,
  marginBottom: spacing.sm,
  justifyContent: "center",
}

const $topPickPill: ViewStyle = {
  flex: 1,
  paddingVertical: 12,
  paddingHorizontal: 14,
  backgroundColor: colors.surfaceSecondary,
  borderRadius: 10,
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "visible",
}

const $topPickContent: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 6,
}

const $topPickTimeText: TextStyle = {
  fontSize: 16,
  fontWeight: "700",
  color: colors.text,
}

const $topPickBadge: ViewStyle = {
  position: "absolute",
  top: -12,
  right: -4,
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.modalHelperText,
  paddingHorizontal: 6,
  paddingVertical: 2,
  borderRadius: 8,
  gap: 3,
}

const $topPickBadgeText: TextStyle = {
  fontSize: 9,
  fontWeight: "700",
  color: colors.surface,
  letterSpacing: 0.3,
}

// ─── Expand / Collapse ──────────────────────────────────────────────

const $expandButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: 12,
  marginBottom: spacing.md,
}

const $expandButtonText: TextStyle = {
  color: colors.modalHelperText,
  fontSize: 14,
  fontWeight: "500",
  marginRight: 4,
}

const $errorContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
  backgroundColor: colors.errorBgLight,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  borderRadius: 8,
  marginTop: spacing.sm,
}

const $errorText: TextStyle = {
  fontSize: 13,
  color: colors.badgeFailedText,
  fontWeight: "500",
}
